import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Factory, CalendarClock, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { BASE, call, get } from "./common";
import SupplierPage from "./SupplierPage";
import SchedulerPage from "./SchedulerPage";

export default function SuppliersTab() {
  const [list, setList] = useState([]);
  const [view, setView] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => get("/suppliers").then((d) => { setList(d); setView((v) => v || d[0]?.id || "scheduler"); }), []);
  useEffect(() => { load(); }, [load]);
  const current = list.find((s) => s.id === view);
  return (
    <div data-testid="suppliers-tab">
      <h1 className="text-3xl font-extrabold">Suppliers</h1>
      <p className="text-muted-foreground mb-5">Supplier feeds, availability rules and one central scheduler — only one supplier job ever runs at a time.</p>
      <div className="flex flex-wrap gap-2 mb-6 border-b pb-3">
        {list.map((s) => (
          <button key={s.id} onClick={() => setView(s.id)} data-testid={`supplier-nav-${s.key}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-colors ${view === s.id ? "bg-steel text-white" : "bg-white border hover:border-accent"}`}>
            <Factory size={15} /> {s.name}
            <span className={`w-2 h-2 rounded-full ${s.status === "active" ? "bg-emerald-400" : "bg-slate-400"}`} />
          </button>))}
        <button onClick={() => setView("scheduler")} data-testid="supplier-nav-scheduler"
          className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-colors ${view === "scheduler" ? "bg-steel text-white" : "bg-white border hover:border-accent"}`}>
          <CalendarClock size={15} /> Scraper Scheduler
        </button>
        <Button variant="ghost" size="sm" onClick={() => setAdding(true)} data-testid="supplier-add-btn"><Plus size={15} className="mr-1" />Add supplier</Button>
      </div>
      {view === "scheduler" ? <SchedulerPage /> : current ? <SupplierPage key={current.id} s={current} reload={load} /> : null}
      <AddSupplier open={adding} onClose={() => setAdding(false)} onDone={(id) => { setAdding(false); load().then(() => setView(id)); }} />
    </div>
  );
}

function AddSupplier({ open, onClose, onDone }) {
  const [f, setF] = useState({ name: "", website: "" });
  const save = async () => {
    const d = await call(() => api.post(`${BASE}/suppliers`, f), "Supplier added");
    if (d) { setF({ name: "", website: "" }); onDone(d.id); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="supplier-add-dialog">
        <DialogHeader><DialogTitle>Add a supplier</DialogTitle>
          <DialogDescription>Uses the same importer: upload their CSV, check the column mapping once, and it is saved for next time. Its schedules start disabled.</DialogDescription></DialogHeader>
        <Input placeholder="Supplier name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="supplier-add-name" />
        <Input placeholder="https://supplier-website" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} data-testid="supplier-add-website" />
        <Button onClick={save} disabled={!f.name.trim()} className="bg-steel text-white" data-testid="supplier-add-save">Add supplier</Button>
      </DialogContent>
    </Dialog>
  );
}
